/**
 * SEE-1388. Highnote validates the application only at final submit. When it rejects a field,
 * `registerOnboard` (speedchain-cdk `highnote_field_errors.py`) rolls the submission back and answers
 * with a GraphQL error of this type whose `errorInfo.fieldErrors` names each rejected field by its
 * draft `formData` path, e.g. `beneficialOwners.2.firstName`. Before this the applicant only saw
 * "could not submit" and resubmitted the same value (2026-09-30: four times).
 *
 * Only paths and codes arrive here, never the rejected value.
 */
export const HIGHNOTE_VALIDATION_ERROR_TYPE = 'HighnoteValidation';

export interface HighnoteFieldError {
  /** Draft `formData` path, or null when the server could not place the error on a field. */
  field: string | null;
  code: string | null;
  description: string | null;
}

/** Thrown by the transport for a `HighnoteValidation` GraphQL error, carrying its field errors. */
export class HighnoteValidationError extends Error {
  readonly fieldErrors: HighnoteFieldError[];

  constructor(message: string, fieldErrors: HighnoteFieldError[]) {
    super(message);
    this.name = 'HighnoteValidationError';
    this.fieldErrors = fieldErrors;
  }
}

const asNullableString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/**
 * The field errors of a `HighnoteValidation` error in a GraphQL `errors` array, or null when the
 * response is anything else. Defensive because it reads a network payload.
 */
export function highnoteFieldErrorsOf(errors: unknown): HighnoteFieldError[] | null {
  if (!Array.isArray(errors)) return null;
  const error = errors.find(
    (e): e is { errorInfo?: { fieldErrors?: unknown } } =>
      !!e &&
      typeof e === 'object' &&
      (e as { errorType?: unknown }).errorType === HIGHNOTE_VALIDATION_ERROR_TYPE
  );
  if (!error) return null;
  const fieldErrors = error.errorInfo?.fieldErrors;
  if (!Array.isArray(fieldErrors)) return [];
  return fieldErrors
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .map((e) => ({
      field: asNullableString(e.field),
      code: asNullableString(e.code),
      description: asNullableString(e.description),
    }));
}
